import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Container, Typography, Paper, Grid, Button, alpha, useTheme } from '@mui/material';
import ConstructionIcon from '@mui/icons-material/Construction';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { Beta } from '../components/ui/Beta';
import { BetaPageStyles } from './BetaPageStyles';
import { BetaSparkleBackground, createSparkles, type Sparkle } from './BetaSparkleBackground';

interface BetaPageProps {
  children?: React.ReactNode;
  title?: string;
  subtitle?: string;
  returnPath?: string;
  returnLabel?: string;
  onReturn?: () => void;
}

const BetaPage: React.FC<BetaPageProps> = ({
  children,
  title = 'Coming Soon',
  subtitle = 'This page is currently in development',
  returnPath = '/',
  returnLabel = 'Return to Backstory',
  onReturn,
}) => {
  const theme = useTheme();
  const [showSparkle, setShowSparkle] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (!children) {
    children = (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Typography>
          The page you requested (<b>{location.pathname.replace(/^\//, '')}</b>) is not yet ready.
        </Typography>
      </Box>
    );
  }

  // Enhanced sparkle effect for background elements
  const [sparkles, setSparkles] = useState<Sparkle[]>([]);

  useEffect(() => {
    setSparkles(createSparkles(30));

    // Show main sparkle effect after a short delay
    const timer = setTimeout(() => {
      setShowSparkle(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleReturn = (): void => {
    if (onReturn) {
      onReturn();
    } else if (returnPath) {
      navigate(returnPath);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: theme.palette.background.default,
        pt: 8,
        pb: 6,
      }}
    >
      <BetaSparkleBackground sparkles={sparkles} theme={theme} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4} direction="column" alignItems="center">
          <Grid size={{ xs: 12 }} sx={{ textAlign: 'center', mb: 2 }}>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                textShadow: `0 0 10px ${alpha(theme.palette.primary.main, 0.3)}`,
                animation: showSparkle ? 'titleGlow 3s ease-in-out infinite alternate' : 'none',
              }}
            >
              {title}
            </Typography>

            <Typography variant="h5" component="h2" color="textSecondary" sx={{ mb: 6 }}>
              {subtitle}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 10, lg: 8 }} sx={{ mb: 4 }}>
            <Paper
              elevation={8}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(8px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Construction icon */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -15,
                  right: -15,
                  bgcolor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                  borderRadius: '50%',
                  p: 2,
                  boxShadow: 3,
                  transform: 'rotate(15deg)',
                }}
              >
                <ConstructionIcon fontSize="large" />
              </Box>

              {/* Content */}
              <Box sx={{ mt: 3, mb: 3 }}>
                {children || (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <RocketLaunchIcon
                      fontSize="large"
                      color="primary"
                      sx={{
                        fontSize: 80,
                        mb: 2,
                        animation: 'rocketWobble 3s ease-in-out infinite',
                      }}
                    />
                    <Typography>
                      We&apos;re working hard to bring you this exciting new feature!
                    </Typography>
                    <Typography color="textSecondary" sx={{ mt: 1 }}>
                      Check back soon for updates.
                    </Typography>
                  </Box>
                )}
                <Beta
                  adaptive={false}
                  sx={{
                    opacity: 0.5,
                    left: '-72px',
                    '& > div': {
                      paddingRight: '30px',
                      background: 'gold',
                      color: '#808080',
                    },
                  }}
                  onClick={(): void => {
                    navigate('/');
                  }}
                />
              </Box>

              {/* Return button */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleReturn}
                  sx={{
                    px: 4,
                    py: 1,
                    borderRadius: 4,
                    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                    '&:hover': {
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                    },
                  }}
                >
                  {returnLabel}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <BetaPageStyles theme={theme} />
    </Box>
  );
};

export { BetaPage };
